import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { useCountry } from '@/lib/country'
import { dateTime } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { useUnsavedChanges } from '@/lib/useUnsavedChanges'
import { AddressFields, TaxIdField } from '@/components/AddressFields'
import { LeaveGuard, useLeaveGuard } from '@/components/LeaveGuard'
import { PicklistField } from '@/components/PicklistField'
import { PartyQuotations } from '@/components/PartyQuotations'
import { AuditLogPanel } from '@/components/AuditLog'
import { PageHeader, Tabs } from '@/components/ui/page'
import { Badge, Busy, Button, Card, CardHeader, EmptyState, ErrorState, Loading } from '@/components/ui/primitives'
import { Dialog } from '@/components/ui/dialog'
import { Checkbox, Field, FormGrid, Input, SaveActions, Select } from '@/components/ui/form'
import { DEFAULT_PAGE_SIZE, FilterSelect, ListToolbar, Pagination } from '@/components/ui/listing'
import { SortableTh, Table, Td, Th, Tr } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type {
  ContactDetail,
  CustomerDetail,
  CustomerListItem,
  CustomerOwner,
  HalalStatus,
  Paged,
  PartyStatus,
  UserListItem,
} from '@/types/api'

/**
 * Who the customer belongs to.
 *
 * Shown to everybody and locked for most, rather than hidden: which rep owns a customer
 * decides what they see tomorrow under `customer.view.own`, so a rep needs to read it even
 * when they may not change it. A field that disappears reads as a missing feature and gets
 * asked about instead.
 *
 * The list of people is only fetched when the field is actually writable - `/users` needs
 * `user.view`, which a Sales Rep does not hold, and asking for it would answer 403 on a
 * control they cannot use anyway.
 */
function SalesRepField({
  countryIsoCode,
  value,
  valueName,
  onChange,
  locked,
  lockedHint,
  hint,
  changed,
}: {
  countryIsoCode: string | undefined
  value: string | null
  valueName: string | null | undefined
  onChange: (next: string) => void
  locked: boolean
  lockedHint: string
  hint: string
  changed?: boolean
}) {
  const people = useAsync<Paged<UserListItem>>(
    (signal) =>
      locked
        ? Promise.resolve({ items: [], page: 1, pageSize: 0, totalCount: 0, totalPages: 0, hasMore: false })
        : api.get<Paged<UserListItem>>('/users', { pageSize: 200 }, signal),
    [locked],
  )

  // Filtered to people who hold this customer's country. Assigning it to somebody who
  // cannot see the country hides the record from the one person now responsible for it -
  // the API answers 409 for exactly this, and the picker should not offer the mistake.
  const eligible = (people.data?.items ?? []).filter(
    (person) => person.isActive && countryIsoCode && person.countries.includes(countryIsoCode),
  )

  if (locked) {
    return (
      <Field label="Sales rep" hint={lockedHint}>
        {(id) => <Input id={id} disabled value={valueName ?? 'Unassigned'} />}
      </Field>
    )
  }

  return (
    <Field label="Sales rep" hint={hint} changed={changed}>
      {(id) => (
        <Select
          id={id}
          value={value ?? ''}
          disabled={people.loading}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">Unassigned</option>
          {/* The current rep may sit outside the eligible list - they can have lost the
              country since. Dropping them silently would rewrite the field to Unassigned
              the moment somebody saved an unrelated change. */}
          {value && !eligible.some((person) => person.id === value) && (
            <option value={value}>{valueName ?? 'Current rep'}</option>
          )}
          {eligible.map((person) => (
            <option key={person.id} value={person.id}>
              {person.displayName}
            </option>
          ))}
        </Select>
      )}
    </Field>
  )
}

/**
 * Where a header's third click lands, and what the toolbar shows for it.
 * A real sort string rather than an empty one, so the two controls always agree.
 */
const DEFAULT_SORT = 'name'

export function Customers() {
  const { can, scope, user } = useAuth()
  const { current } = useCountry()
  const canSeeOthers = scope('customer.view') !== 'own'
  const [search, setSearch] = useState('')

  /**
   * '' is everyone the caller can see, 'mine' is the caller, anything else is one person's
   * id. One control rather than a checkbox beside a picker, because it answers one question
   * - whose accounts am I looking at - and every answer to it is exclusive.
   */
  const [owner, setOwner] = useState('')
  const [status, setStatus] = useState('')
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [creating, setCreating] = useState(false)
  const navigate = useNavigate()
  const toast = useToast()

  const list = useAsync<Paged<CustomerListItem>>(
    (signal) =>
      api.get<Paged<CustomerListItem>>(
        '/customers',
        {
          countryId: current?.countryId,
          search,
          mineOnly: owner === 'mine',
          ownerUserId: owner === '' || owner === 'mine' ? undefined : owner,
          status,
          sort,
          page,
          pageSize,
        },
        signal,
      ),
    [current?.countryId, search, owner, status, sort, page, pageSize],
  )

  /**
   * Everybody who owns a customer this caller can read, from the same scoped query the
   * listing runs - so the menu can never name somebody whose accounts it would then
   * withhold. It follows the country for the same reason: a name offered against a listing
   * it returns nothing in is a name that reads as a bug.
   */
  const owners = useAsync<CustomerOwner[]>(
    (signal) =>
      canSeeOthers
        ? api.get<CustomerOwner[]>('/customers/owners', { countryId: current?.countryId }, signal)
        : Promise.resolve([]),
    [canSeeOthers, current?.countryId],
  )

  // Switching country can take the chosen person out of the menu - and a select whose value
  // matches no option renders blank while still filtering by it, which reads as a listing
  // that has broken. Falling back to everyone is the honest answer: this caller can no
  // longer ask that question here.
  useEffect(() => {
    if (owner === '' || owner === 'mine' || !owners.data) return
    if (owners.data.some((person) => person.userId === owner)) return

    setOwner('')
    setPage(1)
  }, [owners.data, owner])

  // Narrowing the rows changes which page one is, so any filter change goes back to the
  // first page. Staying on page 4 of a result that now has two is an empty screen that
  // looks like no matches.
  function refine(apply: () => void) {
    apply()
    setPage(1)
  }

  const shown = list.data?.items ?? []


  return (
    <>
      <PageHeader
        eyebrow={current?.name}
        title="Customers"
        hint="A customer belongs to one country. Quoting a Singapore customer on a Malaysian letterhead is the mistake this product exists to prevent."
        actions={
          can('customer.create') && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              New customer
            </Button>
          )
        }
      />

      <ListToolbar
        sort={sort}
        onSortChange={(next) => refine(() => setSort(next))}
        sortChoices={[
          { value: 'name', label: 'Name A–Z' },
          { value: '-name', label: 'Name Z–A' },
          { value: 'industry', label: 'Industry' },
          { value: 'status', label: 'Status' },
          { value: '-created', label: 'Newest first' },
          { value: 'created', label: 'Oldest first' },
        ]}
      >
        <div className="w-64">
          <Input
            placeholder="Search company or contact"
            value={search}
            onChange={(event) => refine(() => setSearch(event.target.value))}
          />
        </div>

        <FilterSelect label="Status" value={status} onChange={(next) => refine(() => setStatus(next))}>
          <option value="">All</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </FilterSelect>

        {/* Only worth offering to somebody who can see more than their own. */}
        {canSeeOthers && (
          <FilterSelect
            label="Owner"
            value={owner}
            width="w-52"
            onChange={(next) => refine(() => setOwner(next))}
          >
            <option value="">Everyone I can see</option>
            <option value="mine">Only mine</option>

            {/* Named people below the two set answers, and the caller is not among them -
                "Only mine" is already that answer, and offering it twice under two labels
                makes the menu look like the two do different things. */}
            {(owners.data ?? []).filter((person) => person.userId !== user?.id).length > 0 && (
              <optgroup label="One person">
                {(owners.data ?? [])
                  .filter((person) => person.userId !== user?.id)
                  .map((person) => (
                    <option key={person.userId} value={person.userId}>
                      {person.displayName}
                    </option>
                  ))}
              </optgroup>
            )}
          </FilterSelect>
        )}
      </ListToolbar>

      {list.error && <ErrorState title={list.error.title} detail={list.error.detail} onRetry={list.reload} />}

      <Busy busy={list.loading}>
        {list.data && (
          <Card>
            {shown.length === 0 ? (
              <EmptyState
                title="No customers yet"
                hint={`Nothing in ${current?.name ?? 'this country'} matching that. On mobile you can scan a namecard straight into a contact.`}
              />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <SortableTh field="name" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                      Company
                    </SortableTh>
                    <Th>Primary contact</Th>
                    <Th>Owner</Th>
                    <Th numeric>Quotations</Th>
                    <SortableTh field="status" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                      Status
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((customer) => (
                    <Tr key={customer.id} onClick={() => navigate(`/customers/${customer.id}`)}>
                      <Td>
                        <span className="font-medium">{customer.companyName}</span>
                        {customer.industry && (
                          <span className="block text-[12px] text-muted">{customer.industry}</span>
                        )}
                      </Td>
                      <Td className="text-muted">
                        {customer.primaryContactName ?? '-'}
                        {customer.primaryContactEmail && (
                          <span className="block text-[12px]">{customer.primaryContactEmail}</span>
                        )}
                      </Td>
                      <Td className="text-muted">{customer.ownerName ?? 'Unassigned'}</Td>
                      <Td numeric>{customer.quotationCount}</Td>
                      <Td>
                        <Badge tone={customer.status === 'Active' ? 'positive' : customer.status === 'Blacklisted' ? 'critical' : 'neutral'}>
                          {customer.status}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}

            <Pagination
              page={list.data.page}
              pageSize={list.data.pageSize}
              totalCount={list.data.totalCount}
              totalPages={list.data.totalPages}
              onPageChange={setPage}
              onPageSizeChange={(next) => refine(() => setPageSize(next))}
            />
          </Card>
        )}
      </Busy>

      <NewCustomer
        open={creating}
        countryId={current?.countryId}
        onClose={() => setCreating(false)}
        onCreated={(name) => {
          setCreating(false)
          toast.show(`${name} added`, 'success')
          list.reload()
        }}
      />
    </>
  )
}

/**
 * A customer on its own route rather than behind a piece of list state, so the record can
 * be linked to, reloaded, and reached with the browser's own back button - and so the
 * sidebar can remember it.
 */
/** What the detail form edits, and therefore what an unsaved change can be about. */
const EDITED = [
  'companyName',
  'businessTypeId',
  'registrationNo',
  'taxIdentificationNo',
  'industry',
  'halalStatus',
  'leadSourceId',
  'website',
  'autoCountDebtorCode',
  'ownerUserId',
  'addressLine1',
  'addressLine2',
  'addressLine3',
  'postcode',
  'addressCountry',
] as const

export function CustomerDetailPage() {
  const { id } = useParams()
  return id ? <Customer customerId={id} /> : <ErrorState title="No customer" detail="That link is missing a customer id." />
}

function Customer({ customerId }: { customerId: string }) {
  const { can } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<Partial<CustomerDetail>>({})
  const [contactDialog, setContactDialog] = useState<{ editing?: ContactDetail } | undefined>()
  const [removingContact, setRemovingContact] = useState<ContactDetail | undefined>()
  const [removing, setRemoving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [retiring, setRetiring] = useState(false)

  /**
   * What the status button has just made it, until the record is next read. The form does
   * not carry the status: it is a button rather than a field, and a stale copy of it
   * riding along with the next save would quietly undo the button.
   */
  const [statusNow, setStatusNow] = useState<PartyStatus | undefined>()
  const [tab, setTab] = useState<'details' | 'contacts' | 'quotations' | 'history'>('details')

  /**
   * Its own request rather than a field in the form, for the same reason the product's
   * is: it is one word that changes what the listing shows, not something typed and
   * saved with an address. Undone by pressing the button again.
   *
   * The answer is kept here rather than re-read, because reloading this page re-seeds the
   * form from the server and would throw away an address somebody is halfway through
   * typing. Nothing else on the record moves when the status does.
   */
  async function setStatus(next: PartyStatus) {
    setRetiring(true)
    try {
      await api.patch(`/customers/${customerId}`, { status: next })
      toast.show(next === 'Active' ? 'Customer is active again' : 'Customer marked inactive', 'success')
      setStatusNow(next)
    } catch (cause) {
      toast.show(
        cause instanceof ApiError ? (cause.detail ?? cause.title) : 'Could not change the status',
        'error',
      )
    } finally {
      setRetiring(false)
    }
  }

  async function remove() {
    setRetiring(true)
    try {
      await api.del(`/customers/${customerId}`)
      toast.show(`${detail.data?.companyName ?? 'Customer'} deleted`, 'success')
      guard.bypass(() => navigate('/customers', { replace: true }))
    } catch (cause) {
      toast.show(
        cause instanceof ApiError ? (cause.detail ?? cause.title) : 'Could not delete that customer',
        'error',
      )
      setRetiring(false)
    }
  }

  async function removeContact() {
    if (!removingContact) return

    setRemoving(true)
    try {
      await api.del(`/contacts/${removingContact.id}`)
      toast.show(`${removingContact.name} removed`, 'success')
      setRemovingContact(undefined)
      detail.reload()
    } catch (cause) {
      toast.show(
        cause instanceof ApiError ? (cause.detail ?? cause.title) : 'Could not remove that contact',
        'error',
      )
    } finally {
      setRemoving(false)
    }
  }

  const detail = useAsync<CustomerDetail>(
    (signal) =>
      api.get<CustomerDetail>(`/customers/${customerId}`, undefined, signal).then((loaded) => {
        setForm(loaded)
        return loaded
      }),
    [customerId],
  )

  const apply = useCallback((values: Partial<CustomerDetail>) => setForm(values), [])
  const changes = useUnsavedChanges(form, detail.data, apply, EDITED)
  const guard = useLeaveGuard(changes)

  function set<K extends keyof CustomerDetail>(key: K, value: CustomerDetail[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  async function save() {
    setSaving(true)
    try {
      // First, and on its own endpoint. Moving a customer to another rep changes what two
      // people can see, and it belongs in the audit trail as that event rather than as a
      // detail edit that happened to include a name. It also has a precondition the rest of
      // this form does not - the new rep must hold the country - so letting it go first
      // means a refusal leaves nothing half-written.
      if (form.ownerUserId !== detail.data?.ownerUserId) {
        await api.post(`/customers/${customerId}/reassign`, { ownerUserId: form.ownerUserId })
      }

      await api.patch(`/customers/${customerId}`, {
        companyName: form.companyName,
        registrationNo: form.registrationNo,
        industry: form.industry,

        // A picklist is cleared by asking for it. A null id on its own only means
        // "unchanged", which is how "Not set" would silently do nothing.
        businessTypeId: form.businessTypeId,
        clearBusinessType: form.businessTypeId === null,
        leadSourceId: form.leadSourceId,
        clearLeadSource: form.leadSourceId === null,
        halalStatus: form.halalStatus,
        addressLine1: form.addressLine1,
        addressLine2: form.addressLine2,
        addressLine3: form.addressLine3,
        postcode: form.postcode,
        addressCountry: form.addressCountry,
        taxIdentificationNo: form.taxIdentificationNo,
        website: form.website,
        autoCountDebtorCode: form.autoCountDebtorCode,
      })

      // Before the reload, so the page is clean the moment the save lands rather than
      // for as long as the round trip takes.
      changes.accept()
      toast.show('Customer saved', 'success')
      detail.reload()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not save', 'error')
    } finally {
      setSaving(false)
    }
  }

  // Only the very first load replaces the screen. A reload after a save keeps what is
  // already on it and puts the spinner over the top - blanking a page somebody just
  // saved is the same jump as a loading block pushing a listing down.
  if (detail.loading && !detail.data) return <Loading />
  if (detail.error) return <ErrorState title={detail.error.title} detail={detail.error.detail} onRetry={detail.reload} />

  const canEdit = detail.data?.canEdit ?? false
  const canReassign = detail.data?.canReassign ?? false
  const status = statusNow ?? detail.data?.status ?? 'Active'
  const active = status === 'Active'

  // Deletion is offered only where it is possible. A customer that has been quoted keeps
  // its quotations pointing at it for good, so the button that retires it is the other
  // one - and a Delete that is always there and usually refuses teaches nobody that.
  const deletable = can('customer.delete') && detail.data?.quotationCount === 0

  return (
    <>
      <PageHeader
        eyebrow={detail.data?.countryIsoCode}
        title={form.companyName ?? 'Customer'}
        back={{ label: 'Back to customers', onClick: () => navigate('/customers') }}
        actions={
          <>
            {/* Two verbs that are not the same thing: inactive stops the company being
                offered for new work and is undone by pressing the button again, deleting
                takes the record off the system and only a customer nobody has quoted
                can be deleted at all. */}
            {canEdit && (
              <Button size="sm" disabled={retiring} onClick={() => void setStatus(active ? 'Inactive' : 'Active')}>
                {active ? 'Make inactive' : 'Make active'}
              </Button>
            )}

            {deletable && (
              <div className="ml-2 flex flex-wrap items-center gap-2">
                <Button size="sm" variant="danger" onClick={() => setDeleting(true)}>
                  Delete
                </Button>
              </div>
            )}
          </>
        }
      />

      {/* State, not a verb: what it is now reads before what you can do about it - and on
          its own line, because a chip standing in a row of buttons reads as one more
          button. Blacklisted is a status this screen shows and does not set. */}
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <Badge tone={active ? 'positive' : status === 'Blacklisted' ? 'critical' : 'neutral'}>
          {status}
        </Badge>
        {!active && status !== 'Blacklisted' && (
          <span className="text-[12px] text-muted">
            Kept for its history and out of the active list. Quotations already raised are untouched.
          </span>
        )}
      </div>

      <Dialog
        open={deleting}
        title={`Delete ${detail.data?.companyName ?? 'this customer'}?`}
        onClose={() => setDeleting(false)}
        footer={
          <>
            <Button size="sm" onClick={() => setDeleting(false)}>
              Keep it
            </Button>
            <Button size="sm" variant="danger" loading={retiring} onClick={() => void remove()}>
              Delete
            </Button>
          </>
        }
      >
        <p className="text-[13px] text-ink-2">
          The company, its contacts and everything scanned into them are removed from the
          system for good. This cannot be undone. Nobody has quoted this customer, so
          nothing points at it - the moment somebody does, marking it inactive is the way
          to retire it instead.
        </p>
      </Dialog>

      <Tabs
        active={tab}
        onSelect={setTab}
        tabs={[
          { id: 'details', label: 'Details' },
          { id: 'contacts', label: 'Contacts', count: detail.data?.contacts.length },
          { id: 'quotations', label: 'Quotations' },
          { id: 'history', label: 'History' },
        ]}
      />

      {tab === 'details' && (
      <Card className="mb-5">
        <CardHeader title="Company" />

        {/* Ordered as somebody reads a company off a namecard and then asks the two
            questions a rep actually needs: what kind of company is this, and how did we
            get to them. Registration and tax numbers sit together because they are
            copied from the same document. */}
        <FormGrid>
          <Field label="Company name" required changed={changes.changed('companyName')}>
            {(id) => (
              <Input
                id={id}
                disabled={!canEdit}
                value={form.companyName ?? ''}
                onChange={(e) => set('companyName', e.target.value)}
              />
            )}
          </Field>
          <PicklistField
            label="Business type"
            hint="The legal form they trade as."
            list="BusinessType"
            countryId={detail.data?.countryId}
            value={form.businessTypeId ?? null}
            valueName={detail.data?.businessTypeName}
            onChange={(next) => set('businessTypeId', next)}
            disabled={!canEdit}
            changed={changes.changed('businessTypeId')}
          />

          <Field label="Registration number" changed={changes.changed('registrationNo')}>
            {(id) => (
              <Input
                id={id}
                disabled={!canEdit}
                value={form.registrationNo ?? ''}
                onChange={(e) => set('registrationNo', e.target.value)}
              />
            )}
          </Field>
          <TaxIdField
            value={form.taxIdentificationNo}
            onChange={(next) => set('taxIdentificationNo', next)}
            disabled={!canEdit}
            changed={changes.changed('taxIdentificationNo')}
            countryIsoCode={detail.data?.countryIsoCode}
          />

          <Field
            label="Industry"
            changed={changes.changed('industry')}
            hint="What they do. Free text - not the legal form."
          >
            {(id) => (
              <Input
                id={id}
                disabled={!canEdit}
                value={form.industry ?? ''}
                onChange={(e) => set('industry', e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Halal certified"
            changed={changes.changed('halalStatus')}
            hint="Unknown until somebody has asked them."
          >
            {(id) => (
              <Select
                id={id}
                disabled={!canEdit}
                value={form.halalStatus ?? 'Unknown'}
                onChange={(e) => set('halalStatus', e.target.value as HalalStatus)}
              >
                <option value="Unknown">Not asked</option>
                <option value="Certified">Yes</option>
                <option value="NotCertified">No</option>
              </Select>
            )}
          </Field>

          <PicklistField
            label="Source of lead"
            hint="Where this one came from."
            list="LeadSource"
            countryId={detail.data?.countryId}
            value={form.leadSourceId ?? null}
            valueName={detail.data?.leadSourceName}
            onChange={(next) => set('leadSourceId', next)}
            disabled={!canEdit}
            changed={changes.changed('leadSourceId')}
          />
          <Field label="Website" changed={changes.changed('website')}>
            {(id) => (
              <Input
                id={id}
                disabled={!canEdit}
                value={form.website ?? ''}
                onChange={(e) => set('website', e.target.value)}
              />
            )}
          </Field>

          <Field
            label="AutoCount debtor code"
            changed={changes.changed('autoCountDebtorCode')}
            hint="Unused until the invoice sync milestone. Recorded now so it is not a data-entry job later."
          >
            {(id) => (
              <Input
                id={id}
                disabled={!canEdit}
                value={form.autoCountDebtorCode ?? ''}
                onChange={(e) => set('autoCountDebtorCode', e.target.value)}
              />
            )}
          </Field>

          <SalesRepField
            countryIsoCode={detail.data?.countryIsoCode}
            value={form.ownerUserId ?? null}
            valueName={detail.data?.ownerName}
            onChange={(next) => set('ownerUserId', next || null)}
            locked={!canReassign}
            lockedHint={`Changed by ${detail.data?.ownerName ? `${detail.data.ownerName}'s manager` : 'their manager'}, a Director of Sales or an Owner.`}
            hint="Moving this customer moves what its new rep sees. Their quotations stay with whoever raised them."
            changed={changes.changed('ownerUserId')}
          />
        </FormGrid>

        {/* Not a form field: nobody types these, and putting them in the grid would
            invite somebody to try.

            Padded like every other card footer rather than left to a margin. It sits
            outside FormGrid, so it inherited no padding at all: the text ran to the
            card's own edge and sat flat on its bottom border. */}
        {detail.data && (
          <p className="border-t border-line-soft px-5 py-3 text-[12px] text-muted">
            Created {dateTime(detail.data.createdAt)}
            {detail.data.createdByName && ` by ${detail.data.createdByName}`}
            {detail.data.modifiedAt && ` \u00b7 last changed ${dateTime(detail.data.modifiedAt)}`}
          </p>
        )}
      </Card>
      )}

      {tab === 'details' && (
      <Card className="mb-5">
        <CardHeader
          title="Address"
          hint="This is the address printed on the quotation, so an empty line collapses rather than printing blank."
        />
        <AddressFields value={form} onChange={set} disabled={!canEdit} changed={changes.changed} />
        {canEdit && (
          <SaveActions
            count={changes.fields.length}
            saving={saving}
            saveLabel="Save customer"
            canSave={Boolean(form.companyName?.trim())}
            onSave={() => void save()}
            onDiscard={changes.discard}
          />
        )}
      </Card>
      )}

      {tab === 'contacts' && (
      <Card>
        <CardHeader
          title="Contacts"
          hint="The primary contact is who the quotation is addressed to."
          action={
            canEdit && (
              <Button size="sm" onClick={() => setContactDialog({})}>
                Add contact
              </Button>
            )
          }
        />
        {detail.data && detail.data.contacts.length === 0 ? (
          <EmptyState title="No contacts yet" hint="A quotation needs somebody to address it to." />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Role</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th>Primary</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {detail.data?.contacts.map((contact) => (
                <tr key={contact.id}>
                  <Td>
                    <span className="font-medium">{contact.name}</span>
                    {/* Worth showing: OCR output is a suggestion the user confirmed, and
                        knowing a field came from a photograph explains a stray character. */}
                    {contact.fromNamecardScan && (
                      <span className="ml-2 font-mono text-[11px] text-faint">scanned</span>
                    )}
                  </Td>
                  <Td className="text-muted">{contact.jobTitle ?? '-'}</Td>
                  <Td className="text-muted">{contact.email ?? '-'}</Td>
                  <Td className="text-muted">{contact.phone ?? '-'}</Td>
                  <Td>{contact.isPrimary && <Badge tone="accent">Primary</Badge>}</Td>
                  <Td>
                    {canEdit && (
                      <span className="flex justify-end gap-2">
                        <Button size="sm" onClick={() => setContactDialog({ editing: contact })}>
                          Edit
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setRemovingContact(contact)}>
                          Remove
                        </Button>
                      </span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      )}

      {tab === 'quotations' && (
        <PartyQuotations
          filter={{ customerId }}
          hint="Everything raised for this customer that you have access to see."
        />
      )}

      {tab === 'history' && (
        <AuditLogPanel entityType="Customer" entityId={customerId} className="" />
      )}

      <Dialog
        open={removingContact !== undefined}
        title={removingContact ? `Remove ${removingContact.name}?` : 'Remove this contact?'}
        onClose={() => setRemovingContact(undefined)}
        footer={
          <>
            <Button size="sm" onClick={() => setRemovingContact(undefined)}>
              Keep them
            </Button>
            <Button size="sm" variant="danger" loading={removing} onClick={() => void removeContact()}>
              Remove
            </Button>
          </>
        }
      >
        {/* Says what happens next rather than asking whether you are sure. Removing the
            primary is the case worth naming: the customer does not end up with contacts
            and nobody to address a quotation to. */}
        <p className="text-[13px] text-ink-2">
          {removingContact?.isPrimary && (detail.data?.contacts.length ?? 0) > 1
            ? 'They are the primary contact, so the next contact added becomes primary in their place.'
            : removingContact?.isPrimary
              ? 'They are the only contact on this customer. A quotation needs somebody to address it to, so add another before raising one.'
              : 'Quotations already raised keep the contact they were addressed to.'}
        </p>
      </Dialog>

      <ContactDialog
        open={contactDialog !== undefined}
        customerId={customerId}
        editing={contactDialog?.editing}
        onClose={() => setContactDialog(undefined)}
        onSaved={() => {
          setContactDialog(undefined)
          detail.reload()
        }}
      />

      <LeaveGuard guard={guard} />
    </>
  )
}

/**
 * What has been quoted to this customer.
 *
 * Scoped like every other read, so a rep sees their own and a manager sees their team's.
 * Two people can open the same customer and count a different number of quotations -
 * that is the access rule working, not a bug.
 */

function NewCustomer({
  open,
  countryId,
  onClose,
  onCreated,
}: {
  open: boolean
  countryId: string | undefined
  onClose: () => void
  onCreated: (name: string) => void
}) {
  const toast = useToast()
  const { can, user } = useAuth()
  const { current } = useCountry()
  const [companyName, setCompanyName] = useState('')
  const [industry, setIndustry] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [ownerUserId, setOwnerUserId] = useState('')
  const [saving, setSaving] = useState(false)

  // A rep creating a customer is answering a question with one possible answer, so they
  // are shown the answer rather than a menu. Anybody who may reassign gets the menu.
  const mayChoose = can('customer.reassign')

  async function create() {
    if (!countryId) return

    setSaving(true)
    try {
      await api.post('/customers', {
        countryId,
        companyName,
        industry: industry || null,

        // Omitted rather than sent as the caller's own id: the API already defaults an
        // absent owner to the caller, and saying it twice invites the two to disagree.
        ownerUserId: mayChoose && ownerUserId ? ownerUserId : null,
        contacts: contactName
          ? [{ name: contactName, email: contactEmail || null, isPrimary: true }]
          : [],
      })

      onCreated(companyName)
      setCompanyName('')
      setIndustry('')
      setContactName('')
      setContactEmail('')
      setOwnerUserId('')
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not add the customer', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="New customer"
      description="Created in the country you are working in."
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={saving}
            disabled={!companyName || !countryId}
            onClick={() => void create()}
          >
            Add customer
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Company name" required>
          {(id) => <Input id={id} value={companyName} onChange={(e) => setCompanyName(e.target.value)} />}
        </Field>
        <SalesRepField
          countryIsoCode={current?.isoCode}
          value={ownerUserId || (user?.id ?? null)}
          valueName={user?.displayName}
          onChange={setOwnerUserId}
          locked={!mayChoose}
          lockedHint="Customers you create are yours. A manager can move one later."
          hint="Only people with access to this country. Defaults to you."
        />
        <Field label="Industry">
          {(id) => <Input id={id} value={industry} onChange={(e) => setIndustry(e.target.value)} />}
        </Field>
        <Field label="Primary contact" hint="Optional now, required before a quotation can be sent.">
          {(id) => <Input id={id} value={contactName} onChange={(e) => setContactName(e.target.value)} />}
        </Field>
        <Field label="Contact email">
          {(id) => (
            <Input id={id} type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
          )}
        </Field>
      </div>
    </Dialog>
  )
}

/**
 * Add or edit a contact.
 *
 * One dialog for both because they ask for exactly the same five things; which it is
 * depends only on whether an existing contact was handed in.
 */
function ContactDialog({
  open,
  customerId,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean
  customerId: string
  /** Absent when adding. */
  editing?: ContactDetail
  onClose: () => void
  onSaved: () => void
}) {
  const toast = useToast()
  const [name, setName] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [isPrimary, setIsPrimary] = useState(false)
  const [saving, setSaving] = useState(false)

  // Reloaded whenever the dialog opens on a different contact, so opening one row after
  // another does not show the row before it.
  useEffect(() => {
    if (!open) return

    setName(editing?.name ?? '')
    setJobTitle(editing?.jobTitle ?? '')
    setEmail(editing?.email ?? '')
    setPhone(editing?.phone ?? '')
    setIsPrimary(editing?.isPrimary ?? false)
  }, [open, editing])

  async function save() {
    setSaving(true)
    try {
      const body = {
        name,
        jobTitle: jobTitle || null,
        email: email || null,
        phone: phone || null,
        isPrimary,
      }

      if (editing) {
        await api.patch(`/contacts/${editing.id}`, body)
      } else {
        await api.post(`/customers/${customerId}/contacts`, body)
      }

      onSaved()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not save the contact', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title={editing ? editing.name : 'Add contact'}
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" loading={saving} disabled={!name} onClick={() => void save()}>
            {editing ? 'Save contact' : 'Add contact'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Name" required>
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label="Job title">
          {(id) => <Input id={id} value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />}
        </Field>
        <Field label="Email">
          {(id) => <Input id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />}
        </Field>
        <Field label="Phone">
          {(id) => <Input id={id} value={phone} onChange={(e) => setPhone(e.target.value)} />}
        </Field>
        <Checkbox
          label="Primary contact"
          checked={isPrimary}
          onChange={(event) => setIsPrimary(event.target.checked)}
        />
      </div>
    </Dialog>
  )
}
